/**
 * useTokenEventSubscription
 *
 * Subscribes to TokenService lifecycle events (refreshed, expired, cleared,
 * auth:error) and delegates to the provided callbacks. Extracted from
 * SecureAuthProvider to keep the provider focused on context wiring.
 */
import { useEffect } from 'react';
import { tokenService } from '../services/TokenService';
import { authLogger } from '@/core/utils/logger';
import { getStorageItem, setStorageItem, STORAGE_KEYS } from '@/core/utils/local-storage';

interface UseTokenEventSubscriptionOptions {
  /** Called when token is refreshed — update local token status */
  onTokenUpdate: () => void;
  /** Called when token expires or is cleared — clear user state */
  onAuthCleared: () => void;
  /** Called on auth:error — typically triggers logout. Error is already logged by the hook. */
  onAuthError: () => void;
  /** Set to false to skip subscription (e.g., lightweight mode) */
  enabled: boolean;
}

export function useTokenEventSubscription({
  onTokenUpdate,
  onAuthCleared,
  onAuthError,
  enabled,
}: UseTokenEventSubscriptionOptions): void {
  useEffect(() => {
    if (!enabled) return;

    const handleTokenRefreshed = () => {
      if (process.env.NODE_ENV === 'development') {
        authLogger.warn('[useTokenEventSubscription] token:refreshed event');
      }
      onTokenUpdate();
    };

    const handleTokenExpired = () => {
      if (process.env.NODE_ENV === 'development') {
        authLogger.warn(
          '[useTokenEventSubscription] token:expired event - this will cause socket disconnect!'
        );
        try {
          const history = (getStorageItem(STORAGE_KEYS.TOKEN_EVENTS) as unknown[] || []) as Record<string, unknown>[];
          history.push({
            event: 'token:expired',
            time: new Date().toISOString(),
          });
          setStorageItem(STORAGE_KEYS.TOKEN_EVENTS, history.slice(-20));
        } catch (_e) {
          /* ignore */
        }
      }
      onTokenUpdate();
      onAuthCleared();
    };

    const handleTokenCleared = () => {
      if (process.env.NODE_ENV === 'development') {
        authLogger.warn(
          '[useTokenEventSubscription] token:cleared event - this will cause socket disconnect!'
        );
        try {
          const history = (getStorageItem(STORAGE_KEYS.TOKEN_EVENTS) as unknown[] || []) as Record<string, unknown>[];
          history.push({
            event: 'token:cleared',
            time: new Date().toISOString(),
          });
          setStorageItem(STORAGE_KEYS.TOKEN_EVENTS, history.slice(-20));
        } catch (_e) {
          /* ignore */
        }
      }
      onTokenUpdate();
      onAuthCleared();
    };

    const handleAuthError = (error: Error) => {
      authLogger.error('[useTokenEventSubscription] Auth error:', error);
      if (process.env.NODE_ENV === 'development') {
        authLogger.warn(
          '[useTokenEventSubscription] auth:error event - logging out!',
          error.message
        );
      }
      onAuthError();
    };

    // Refresh lifecycle events — keep tokenStatus.hasRefreshInProgress in sync
    const handleRefreshLifecycle = () => {
      onTokenUpdate();
    };

    tokenService.on('token:refreshed', handleTokenRefreshed);
    tokenService.on('token:expired', handleTokenExpired);
    tokenService.on('token:cleared', handleTokenCleared);
    tokenService.on('auth:error', handleAuthError);
    tokenService.on('token:refreshing', handleRefreshLifecycle);
    tokenService.on('token:refresh-settled', handleRefreshLifecycle);

    return () => {
      tokenService.off('token:refreshed', handleTokenRefreshed);
      tokenService.off('token:expired', handleTokenExpired);
      tokenService.off('token:cleared', handleTokenCleared);
      tokenService.off('auth:error', handleAuthError);
      tokenService.off('token:refreshing', handleRefreshLifecycle);
      tokenService.off('token:refresh-settled', handleRefreshLifecycle);
    };
  }, [onTokenUpdate, onAuthCleared, onAuthError, enabled]);
}
